import { Text, clx } from "@medusajs/ui"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import React from "react"

type AccordionItemProps = AccordionPrimitive.AccordionItemProps & {
  title: string
  subtitle?: string
  description?: string
  required?: boolean
  tooltip?: string
  forceMountContent?: true
  headingSize?: "small" | "medium" | "large"
  customTrigger?: React.ReactNode
  complete?: boolean
  active?: boolean
  triggerable?: boolean
  children: React.ReactNode
}

type AccordionProps =
  | (AccordionPrimitive.AccordionSingleProps &
      React.RefAttributes<HTMLDivElement>)
  | (AccordionPrimitive.AccordionMultipleProps &
      React.RefAttributes<HTMLDivElement>)

const Accordion: React.FC<AccordionProps> & {
  Item: React.FC<AccordionItemProps>
} = ({ children, ...props }) => {
  const rootProps: Record<string, any> = { ...props }

  // Ensure a `type` prop exists when using multiple items — default to 'multiple'
  if (!rootProps.type) {
    // If multiple Accordion.Items present, default to multiple; otherwise single.
    const childCount = React.Children.count(children)
    rootProps.type = childCount > 1 ? "multiple" : "single"
  }

  // If in multiple mode and no defaultValue was provided, open all child items by default.
  if ((rootProps as any).type === "multiple" && !(rootProps as any).defaultValue) {
    const childArray = React.Children.toArray(children)
    const values: string[] = []

    childArray.forEach((child: any) => {
      // look for AccordionPrimitive.Item-like children which should have a 'value' prop
      if (child && child.props && child.props.value) {
        values.push(child.props.value)
      }
    })

    if (values.length > 0) {
      rootProps.defaultValue = values
    }
  }

  return (
    /* x@ts-expect-error */
    <AccordionPrimitive.Root {...(rootProps as any)}>{children}</AccordionPrimitive.Root>
  )
}

const Item: React.FC<AccordionItemProps> = ({
  title,
  subtitle,
  description,
  children,
  className,
  headingSize = "large",
  customTrigger = undefined,
  forceMountContent = undefined,
  triggerable,
  ...props
}) => {
  return (
    /* x@ts-expect-error */
    <AccordionPrimitive.Item
      {...props}
      className={clx(
        // use design system border token instead of hardcoded grey
        "border-ui-border-base group border-t last:mb-0 last:border-b",
        "py-3",
        className
      )}
    >
      {/* x@ts-expect-error */}
      <AccordionPrimitive.Header className="px-1">
        <div className="flex flex-col">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-4">
              <Text className="text-ui-fg-subtle text-sm">{title}</Text>
            </div>
            {/* x@ts-expect-error */}
            <AccordionPrimitive.Trigger>
              {customTrigger || <MorphingTrigger />}
            </AccordionPrimitive.Trigger>
          </div>
          {subtitle && (
            <Text as="span" size="small" className="mt-1">
              {subtitle}
            </Text>
          )}
        </div>
      </AccordionPrimitive.Header>
      {/* x@ts-expect-error */}
      <AccordionPrimitive.Content
        forceMount={forceMountContent}
        className={clx(
          "radix-state-closed:animate-accordion-close radix-state-open:animate-accordion-open radix-state-closed:pointer-events-none px-1"
        )}
      >
        <div className="inter-base-regular group-radix-state-closed:animate-accordion-close">
          {description && <Text>{description}</Text>}
          <div className="w-full">{children}</div>
        </div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  )
}

Accordion.Item = Item

const MorphingTrigger = () => {
  return (
  <div className="text-ui-fg-subtle hover:bg-ui-bg-field-hover active:bg-ui-bg-field-hover active:text-primary focus:border-primary disabled:text-ui-fg-muted bg-transparent disabled:bg-transparent rounded-rounded group relative p-[6px]">
      <div className="h-5 w-5">
        <span className="bg-grey-50 rounded-circle group-radix-state-open:rotate-90 absolute inset-y-[31.75%] left-[48%] right-1/2 w-[1.5px] duration-300" />
        <span className="bg-grey-50 rounded-circle group-radix-state-open:rotate-90 group-radix-state-open:left-1/2 group-radix-state-open:right-1/2 absolute inset-x-[31.75%] top-[48%] bottom-1/2 h-[1.5px] duration-300" />
      </div>
    </div>
  )
}

export default Accordion
